// Structured editor for the DB-driven storefront content blocks (Neon page_content).
// Reused inside the PDP (Product) and PCP (Category) tabs. Edits the blocks[] array
// with a typed form per block type — no HTML/JSON. Images upload straight to R2.
// The cpx-tcp theme renders whatever is saved here at render time (page-content fn).
// KB: project_db_driven_storefront.md.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, RefreshCw, Check, AlertTriangle, Trash2, ArrowUp, ArrowDown, Plus, Upload, ExternalLink } from 'lucide-react';
import { neonQuery } from '../../services/api';
import { getAccessToken } from '../../services/auth';
import { BASE_URL, readFileAsBase64 } from './siteContentShared';

// Upload a file to R2 via the r2-upload function; returns the public r2-image URL.
async function uploadToR2(file) {
  const dataUrl = await readFileAsBase64(file);
  const base64 = String(dataUrl).split(',')[1] || '';
  const res = await fetch(BASE_URL + '/r2-upload', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, contentType: file.type || 'image/jpeg', data: base64 }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || !d.url) throw new Error(d.error || 'R2 upload failed');
  return d.url;
}

const uid = (p) => p + '-' + Math.random().toString(36).slice(2, 8);

// Default shape per block type — mirrors the theme renderer expectations.
const BLOCK_DEFAULTS = {
  'os-hero': () => ({ type: 'os-hero', eyebrow: '', heading: '', body: [{ kind: 'lead', text: '' }], est: '', clip: { image: { url: '', alt: '' }, link: '', linkAria: '', caption: '' } }),
  'os-story': () => ({ type: 'os-story', heading: '', paragraphs: [''] }),
  'os-stats': () => ({ type: 'os-stats', tiles: [{ num: '', label: '' }] }),
  'os-place': () => ({ type: 'os-place', image: { url: '', alt: '' }, heading: '', paragraphs: [''], buttons: [{ text: '', link: '' }] }),
  'content-block': () => ({ type: 'content-block', id: uid('cb'), heading: '', copy: '', image: { url: '', alt: '' }, layout: { bodyTextPosition: 'body-left', customClass: '' } }),
  'content-block-2image': () => ({ type: 'content-block-2image', id: uid('cb2'), heading: '', content: '', images: [{ url: '', alt: '' }, { url: '', alt: '' }] }),
  'details-block': () => ({ type: 'details-block', id: uid('d'), contentHeading: '', contentCopy: '', layout: { bodyTextPosition: 'body-left', customClass: '' }, options: [] }),
  'image-text-box': () => ({ type: 'image-text-box', id: uid('itb'), title: '', description: '', imageAlt: '', showMobileImage: true, image: { url: '' }, mobileImage: { url: '' }, button: { title: '', link: '' } }),
  'as-seen-in': () => ({ type: 'as-seen-in', id: uid('asi'), title: '', logos: [] }),
  'recipe-slider': () => ({ type: 'recipe-slider', id: uid('rs'), title: 'Recipes', button: { title: '', link: '', type: 'button--primary' } }),
  'hero-slider': () => ({ type: 'hero-slider', id: uid('hero'), slides: [] }),
  'shop-by-category': () => ({ type: 'shop-by-category', id: uid('shop'), title: '', categories: [] }),
  'best-sellers': () => ({ type: 'best-sellers', id: uid('best'), title: 'Best Sellers', products: [] }),
  'community': () => ({ type: 'community', id: uid('comm'), title: '', description: '', photos: [] }),
  'spacer': () => ({ type: 'spacer', id: uid('sp'), size: '40px' }),
  'faq': () => ({ type: 'faq', id: uid('faq'), heading: '', items: [] }),
  'faq-v2': () => ({ type: 'faq-v2', id: uid('fv2'), title: '', items: [] }),
  'webpage-content': () => ({ type: 'webpage-content', id: uid('wc'), title: '', intro: '', items: [] }),
  'og-banner': () => ({ type: 'og-banner', id: uid('ogb'), image: { url: '' }, description: '' }),
  'content-with-contact': () => ({ type: 'content-with-contact', id: uid('cwc'), title: '', description: '' }),
  'image-with-legends': () => ({ type: 'image-with-legends', id: uid('iwl'), image: { url: '' }, legends: [] }),
  'cms-banner': () => ({ type: 'cms-banner', id: uid('cms'), title: '', description: '', image: { url: '' }, mobileImage: { url: '' } }),
  'au-hero': () => ({ type: 'au-hero', id: uid('auh'), eyebrow: '', heading: '', leads: [], image: { url: '', alt: '' } }),
  'au-proof': () => ({ type: 'au-proof', id: uid('aup'), badge: '', lead: '', tiles: [], cta: { text: '', link: '' } }),
  'au-do': () => ({ type: 'au-do', id: uid('aud'), heading: '', sub: '', columns: [] }),
  'au-why': () => ({ type: 'au-why', id: uid('auw'), image: { url: '', alt: '' }, heading: '', items: [] }),
  'au-ship': () => ({ type: 'au-ship', id: uid('aus'), heading: '', paragraphs: [], buttons: [], image: { url: '', alt: '' } }),
  'footer-column': () => ({ type: 'footer-column', items: [{ text: '', link: '' }] }),
  'nav-image': () => ({ type: 'nav-image', image: { url: '', alt: '' }, height: 305 }),
  'nav-section': () => ({ type: 'nav-section', items: [{ name: '', link: '' }] }),
  'html': () => ({ type: 'html', html: '' }),
};
const BLOCK_LABELS = {
  'os-hero': 'Our Story — Hero',
  'os-story': 'Our Story — Story section',
  'os-stats': 'Our Story — Stat band',
  'os-place': 'Our Story — Place / closing',
  'content-block': 'Content Block (image + copy)',
  'content-block-2image': 'Content Block (2 images)',
  'details-block': 'Details / Accordion',
  'image-text-box': 'Image + Text Box',
  'as-seen-in': 'As Seen In (logos)',
  'recipe-slider': 'Recipe Slider',
  'hero-slider': 'Hero Slider', 'shop-by-category': 'Shop By Category', 'best-sellers': 'Best Sellers', 'community': 'Community Gallery', 'spacer': 'Spacer', 'faq': 'FAQ (accordion)', 'faq-v2': 'FAQ v2', 'webpage-content': 'Webpage Content', 'og-banner': 'Banner (image + text)', 'content-with-contact': 'Contact block', 'image-with-legends': 'Image with legend', 'cms-banner': 'Page Banner (hero image)', 'au-hero': 'About: Hero', 'au-proof': 'About: Proof / stats', 'au-do': 'About: Columns', 'au-why': 'About: Image + list', 'au-ship': 'About: Text + image + buttons',
  'footer-column': 'Footer column / social',
  'nav-image': 'Nav promo image',
  'nav-section': 'Content-page sub-nav',
  'html': 'Raw HTML (advanced)',
};

// ── small field primitives ──────────────────────────────────────────────────
const L = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', margin: '8px 0 3px' };
const IN = { width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 5, boxSizing: 'border-box' };

function Text({ label, value, onChange, placeholder }) {
  return (<div><label style={L}>{label}</label><input style={IN} value={value || ''} placeholder={placeholder || ''} onChange={e => onChange(e.target.value)} /></div>);
}
function Area({ label, value, onChange, rows }) {
  return (<div><label style={L}>{label}</label><textarea style={{ ...IN, minHeight: (rows || 3) * 20, fontFamily: 'inherit', resize: 'vertical' }} value={value || ''} onChange={e => onChange(e.target.value)} /></div>);
}
function Select({ label, value, onChange, options }) {
  return (<div><label style={L}>{label}</label><select style={IN} value={value || ''} onChange={e => onChange(e.target.value)}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>);
}

// Image field: R2 upload + URL text + thumbnail preview.
function ImageField({ label, url, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try { const u = await uploadToR2(file); onChange(u); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  return (
    <div>
      <label style={L}>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {url ? <img src={url} alt="" style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 5, border: '1px solid #e5e7eb', flex: '0 0 auto' }} /> : <div style={{ width: 54, height: 54, borderRadius: 5, border: '1px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', flex: '0 0 auto', fontSize: 10 }}>none</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <input style={{ ...IN, fontSize: 11.5 }} value={url || ''} placeholder="R2 image URL" onChange={e => onChange(e.target.value)} />
          <div style={{ marginTop: 5 }}>
            <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => pick(e.target.files && e.target.files[0])} />
            <button type="button" onClick={() => ref.current && ref.current.click()} disabled={busy} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: busy ? 'default' : 'pointer' }}>
              {busy ? <RefreshCw size={12} className="spin" /> : <Upload size={12} />} {busy ? 'Uploading to R2…' : 'Upload to R2'}
            </button>
            {err && <span style={{ color: '#b91c1c', fontSize: 11, marginLeft: 8 }}>{err}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── per-block-type form ─────────────────────────────────────────────────────
function BlockForm({ block, onChange }) {
  const b = block;
  const patch = (upd) => onChange({ ...b, ...upd });
  const patchLayout = (upd) => onChange({ ...b, layout: { ...(b.layout || {}), ...upd } });
  const posOpts = [{ value: 'body-left', label: 'Text left / image right' }, { value: 'body-right', label: 'Text right / image left' }];
  const itemBox = { border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 6, background: '#fafafa' };
  const itemHead = (i, label, onDel) => (<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: 11, color: '#6b7280' }}>{label} {i + 1}</span><button type="button" onClick={onDel} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer' }}><Trash2 size={13} /></button></div>);
  const addBtn = (label, onClick) => (<button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer' }}><Plus size={12} /> {label}</button>);

  if (b.type === 'footer-column') { const its = b.items || [];
    const setI = (i, u) => { const n = its.slice(); n[i] = { ...n[i], ...u }; patch({ items: n }); };
    return (<>
      <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>
        One list. Tick <b>Heading</b> for a column title; leave it unticked for a link.
        For social icons, set an <b>Icon</b> (e.g. <code>#icon-atf-facebook</code>) and leave text blank.
      </p>
      {its.map((it, i) => (<div key={i} style={itemBox}>
        {itemHead(i, it.heading ? 'Heading' : 'Link', () => patch({ items: its.filter((_, k) => k !== i) }))}
        <Text label="Text" value={it.text} onChange={v => setI(i, { text: v })} />
        <Text label="Link" value={it.link} onChange={v => setI(i, { link: v })} />
        <Text label="Icon (optional, e.g. #icon-atf-facebook)" value={it.icon} onChange={v => setI(i, { icon: v })} />
        <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
          <label style={{ ...L, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <input type="checkbox" checked={!!it.heading} onChange={e => setI(i, { heading: e.target.checked || undefined })} /> Heading
          </label>
          <label style={{ ...L, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
            <input type="checkbox" checked={!!it.newTab} onChange={e => setI(i, { newTab: e.target.checked || undefined })} /> Open in new tab
          </label>
        </div>
      </div>))}
      {addBtn('Add row', () => patch({ items: [...its, { text: '', link: '' }] }))}
    </>); }

  if (b.type === 'nav-image') return (<>
    <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
    <Text label="Alt text" value={b.image && b.image.alt} onChange={v => patch({ image: { ...(b.image || {}), alt: v } })} />
    <Text label="Height (px)" value={b.height != null ? String(b.height) : ''} onChange={v => patch({ height: v ? parseInt(v, 10) || undefined : undefined })} />
  </>);

  if (b.type === 'nav-section') { const its = b.items || [];
    const setI = (i, u) => { const n = its.slice(); n[i] = { ...n[i], ...u }; patch({ items: n }); };
    return (<>
      <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>The current page's link is auto-highlighted by URL.</p>
      {its.map((it, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Link', () => patch({ items: its.filter((_, k) => k !== i) }))}
        <Text label="Name" value={it.name} onChange={v => setI(i, { name: v })} />
        <Text label="Link" value={it.link} onChange={v => setI(i, { link: v })} />
      </div>))}
      {addBtn('Add link', () => patch({ items: [...its, { name: '', link: '' }] }))}
    </>); }

  if (b.type === 'os-hero') { const bd = b.body || []; const c = b.clip || {};
    const setB = (i, u) => { const n = bd.slice(); n[i] = { ...n[i], ...u }; patch({ body: n }); };
    const setC = (u) => patch({ clip: { ...c, ...u } });
    return (<>
      <Text label="Eyebrow" value={b.eyebrow} onChange={v => patch({ eyebrow: v })} />
      <Text label="Heading (HTML allowed; <em> renders orange)" value={b.heading} onChange={v => patch({ heading: v })} />
      <label style={L}>Body</label>
      {bd.map((p, i) => (<div key={i} style={itemBox}>
        {itemHead(i, p.kind === 'pull' ? 'Pull-quote' : 'Paragraph', () => patch({ body: bd.filter((_, k) => k !== i) }))}
        <label style={L}>Style</label>
        <select style={IN} value={p.kind || 'lead'} onChange={e => setB(i, { kind: e.target.value })}>
          <option value="lead">Paragraph</option>
          <option value="pull">Pull-quote (orange rule)</option>
        </select>
        <Area label="Text (HTML allowed)" value={p.text} rows={3} onChange={v => setB(i, { text: v })} />
      </div>))}
      {addBtn('Add paragraph', () => patch({ body: [...bd, { kind: 'lead', text: '' }] }))}
      <Text label="Est. line" value={b.est} onChange={v => patch({ est: v })} />
      <label style={L}>Newspaper clipping</label>
      <div style={itemBox}>
        <ImageField label="Image" url={c.image && c.image.url} onChange={v => setC({ image: { ...(c.image || {}), url: v } })} />
        <Text label="Image alt text" value={c.image && c.image.alt} onChange={v => setC({ image: { ...(c.image || {}), alt: v } })} />
        <Text label="Link" value={c.link} onChange={v => setC({ link: v })} />
        <Text label="Link aria-label" value={c.linkAria} onChange={v => setC({ linkAria: v })} />
        <Area label="Caption (HTML allowed)" value={c.caption} rows={2} onChange={v => setC({ caption: v })} />
      </div>
    </>); }

  if (b.type === 'os-story') { const ps = b.paragraphs || [];
    const setP = (i, v) => { const n = ps.slice(); n[i] = v; patch({ paragraphs: n }); };
    return (<>
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <label style={L}>Paragraphs</label>
      {ps.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Para', () => patch({ paragraphs: ps.filter((_, k) => k !== i) }))}
        <Area label="Text (HTML allowed)" value={t} rows={4} onChange={v => setP(i, v)} />
      </div>))}
      {addBtn('Add paragraph', () => patch({ paragraphs: [...ps, ''] }))}
    </>); }

  if (b.type === 'os-stats') { const ts = b.tiles || [];
    const setT = (i, u) => { const n = ts.slice(); n[i] = { ...n[i], ...u }; patch({ tiles: n }); };
    return (<>
      <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px' }}>
        A tile wrapped in <code>&lt;!--RVCOUNT--&gt;59,000+&lt;!--/RVCOUNT--&gt;</code> is refreshed nightly
        from the live review total &mdash; edit the label, leave the markers in place.
      </p>
      {ts.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Tile', () => patch({ tiles: ts.filter((_, k) => k !== i) }))}
        <Text label="Number (HTML allowed)" value={t.num} onChange={v => setT(i, { num: v })} />
        <Text label="Label" value={t.label} onChange={v => setT(i, { label: v })} />
      </div>))}
      {addBtn('Add tile', () => patch({ tiles: [...ts, { num: '', label: '' }] }))}
    </>); }

  if (b.type === 'os-place') { const ps = b.paragraphs || []; const bs = b.buttons || [];
    const setP = (i, v) => { const n = ps.slice(); n[i] = v; patch({ paragraphs: n }); };
    const setB2 = (i, u) => { const n = bs.slice(); n[i] = { ...n[i], ...u }; patch({ buttons: n }); };
    return (<>
      <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
      <Text label="Image alt text" value={b.image && b.image.alt} onChange={v => patch({ image: { ...(b.image || {}), alt: v } })} />
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <label style={L}>Paragraphs</label>
      {ps.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Para', () => patch({ paragraphs: ps.filter((_, k) => k !== i) }))}
        <Area label="Text (HTML allowed)" value={t} rows={3} onChange={v => setP(i, v)} />
      </div>))}
      {addBtn('Add paragraph', () => patch({ paragraphs: [...ps, ''] }))}
      <label style={L}>Buttons</label>
      {bs.map((bt, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Button', () => patch({ buttons: bs.filter((_, k) => k !== i) }))}
        <Text label="Text" value={bt.text} onChange={v => setB2(i, { text: v })} />
        <Text label="Link" value={bt.link} onChange={v => setB2(i, { link: v })} />
        <label style={{ ...L, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={!!bt.ghost} onChange={e => setB2(i, { ghost: e.target.checked })} />
          Outline style (secondary)
        </label>
      </div>))}
      {addBtn('Add button', () => patch({ buttons: [...bs, { text: '', link: '' }] }))}
    </>); }


  if (b.type === 'content-block') return (<>
    <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
    <Area label="Copy (HTML allowed)" value={b.copy} rows={4} onChange={v => patch({ copy: v })} />
    <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
    <Select label="Layout" value={(b.layout || {}).bodyTextPosition} onChange={v => patchLayout({ bodyTextPosition: v })} options={posOpts} />
    <Text label="Custom class (e.g. 'guaranteed' for the happy-carousel)" value={(b.layout || {}).customClass} onChange={v => patchLayout({ customClass: v })} />
  </>);

  if (b.type === 'content-block-2image') { const im = b.images || [{}, {}]; const setImg = (i, url) => { const n = [im[0] || {}, im[1] || {}]; n[i] = { ...n[i], url }; patch({ images: n }); };
    return (<>
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <Area label="Content (HTML allowed)" value={b.content} rows={4} onChange={v => patch({ content: v })} />
      <ImageField label="Image 1" url={(im[0] || {}).url} onChange={u => setImg(0, u)} />
      <ImageField label="Image 2" url={(im[1] || {}).url} onChange={u => setImg(1, u)} />
    </>); }

  if (b.type === 'details-block') { const opts = b.options || []; const setOpt = (i, upd) => { const n = opts.slice(); n[i] = { ...n[i], ...upd }; patch({ options: n }); };
    return (<>
      <Text label="Heading" value={b.contentHeading} onChange={v => patch({ contentHeading: v })} />
      <Area label="Intro copy (HTML allowed)" value={b.contentCopy} rows={3} onChange={v => patch({ contentCopy: v })} />
      <label style={L}>Accordion items</label>
      {opts.map((o, i) => (<div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 6, background: '#fafafa' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 11, color: '#6b7280' }}>Item {i + 1}</span><button type="button" onClick={() => patch({ options: opts.filter((_, k) => k !== i) })} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer' }}><Trash2 size={13} /></button></div>
        <Text label="Item heading" value={o.heading} onChange={v => setOpt(i, { heading: v })} />
        <Area label="Item copy (HTML allowed)" value={o.copy} rows={2} onChange={v => setOpt(i, { copy: v })} />
        <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4 }}><input type="checkbox" checked={!!o.recipe} onChange={e => setOpt(i, { recipe: e.target.checked })} /> Show recipe connector</label>
      </div>))}
      <button type="button" onClick={() => patch({ options: [...opts, { heading: '', copy: '', recipe: false }] })} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer' }}><Plus size={12} /> Add item</button>
    </>); }

  if (b.type === 'image-text-box') return (<>
    <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
    <Area label="Description (HTML allowed)" value={b.description} rows={4} onChange={v => patch({ description: v })} />
    <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
    <ImageField label="Mobile image (optional)" url={b.mobileImage && b.mobileImage.url} onChange={v => patch({ mobileImage: { ...(b.mobileImage || {}), url: v } })} />
    <Text label="Button text" value={(b.button || {}).title} onChange={v => patch({ button: { ...(b.button || {}), title: v } })} />
    <Text label="Button link" value={(b.button || {}).link} onChange={v => patch({ button: { ...(b.button || {}), link: v } })} />
  </>);

  if (b.type === 'as-seen-in') { const logos = b.logos || []; const setLogo = (i, upd) => { const n = logos.slice(); n[i] = { ...n[i], ...upd }; patch({ logos: n }); };
    return (<>
      <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
      <label style={L}>Logos</label>
      {logos.map((lg, i) => (<div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 6, background: '#fafafa' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: 11, color: '#6b7280' }}>Logo {i + 1}</span><button type="button" onClick={() => patch({ logos: logos.filter((_, k) => k !== i) })} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer' }}><Trash2 size={13} /></button></div>
        <ImageField label="Logo image" url={(lg.image || {}).url} onChange={u => setLogo(i, { image: { ...(lg.image || {}), url: u } })} />
        <Text label="Alt text" value={lg.alt} onChange={v => setLogo(i, { alt: v })} />
      </div>))}
      <button type="button" onClick={() => patch({ logos: [...logos, { image: { url: '' }, alt: '' }] })} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer' }}><Plus size={12} /> Add logo</button>
    </>); }

  if (b.type === 'recipe-slider') return (<>
    <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
    <Text label="Button text" value={(b.button || {}).title} onChange={v => patch({ button: { ...(b.button || {}), title: v } })} />
    <Text label="Button link" value={(b.button || {}).link} onChange={v => patch({ button: { ...(b.button || {}), link: v } })} />
    <p style={{ fontSize: 11.5, color: '#6b7280', margin: '6px 0 0' }}>Recipe cards are fetched automatically by product SKU at render time.</p>
  </>);


  if (b.type === 'hero-slider') { const sl = b.slides || []; const set = (i, u) => { const n = sl.slice(); n[i] = { ...n[i], ...u }; patch({ slides: n }); };
    return (<>
      <label style={L}>Slides</label>
      {sl.map((s, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Slide', () => patch({ slides: sl.filter((_, k) => k !== i) }))}
        <Text label="Subtitle" value={s.subTitle} onChange={v => set(i, { subTitle: v })} />
        <Text label="Title" value={s.title} onChange={v => set(i, { title: v })} />
        <ImageField label="Image" url={s.image && s.image.url} onChange={v => set(i, { image: { ...(s.image || {}), url: v } })} />
        <Text label="Button text" value={s.buttonTitle} onChange={v => set(i, { buttonTitle: v })} />
        <Text label="Button link" value={s.buttonLink} onChange={v => set(i, { buttonLink: v })} />
      </div>))}
      {addBtn('Add slide', () => patch({ slides: [...sl, { subTitle: '', title: '', buttonTitle: '', buttonLink: '', image: { url: '' }, imageAlt: '' }] }))}
    </>); }

  if (b.type === 'shop-by-category') { const cs = b.categories || []; const set = (i, u) => { const n = cs.slice(); n[i] = { ...n[i], ...u }; patch({ categories: n }); };
    return (<>
      <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
      <label style={L}>Category tiles</label>
      {cs.map((c, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Tile', () => patch({ categories: cs.filter((_, k) => k !== i) }))}
        <Text label="Name" value={c.name} onChange={v => set(i, { name: v })} />
        <Text label="Link" value={c.link} onChange={v => set(i, { link: v })} />
        <ImageField label="Image" url={c.image && c.image.url} onChange={v => set(i, { image: { ...(c.image || {}), url: v } })} />
      </div>))}
      {addBtn('Add tile', () => patch({ categories: [...cs, { name: '', link: '', image: { url: '' } }] }))}
    </>); }

  if (b.type === 'best-sellers') { const ps = b.products || []; const set = (i, u) => { const n = ps.slice(); n[i] = { ...n[i], ...u }; patch({ products: n }); };
    return (<>
      <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
      <label style={L}>Products</label>
      {ps.map((pr, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Product', () => patch({ products: ps.filter((_, k) => k !== i) }))}
        <Text label="Name" value={pr.name} onChange={v => set(i, { name: v })} />
        <Text label="Link (product URL)" value={pr.url} onChange={v => set(i, { url: v })} />
        <ImageField label="Image" url={pr.image && pr.image.url} onChange={v => set(i, { image: { ...(pr.image || {}), url: v } })} />
        <Text label="Price" value={pr.price} onChange={v => set(i, { price: v })} />
        <Text label="Sale price (optional)" value={pr.salePrice} onChange={v => set(i, { salePrice: v })} />
        <Text label="Tag (e.g. Sale)" value={pr.tag} onChange={v => set(i, { tag: v })} />
      </div>))}
      {addBtn('Add product', () => patch({ products: [...ps, { name: '', url: '', image: { url: '' }, price: '', salePrice: '', tag: '' }] }))}
    </>); }

  if (b.type === 'community') { const ph = b.photos || []; const set = (i, u) => { const n = ph.slice(); n[i] = { ...n[i], ...u }; patch({ photos: n }); };
    return (<>
      <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
      <Area label="Description (HTML allowed)" value={b.description} rows={3} onChange={v => patch({ description: v })} />
      <label style={L}>Photos (first = large, rest = grid)</label>
      {ph.map((x, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Photo', () => patch({ photos: ph.filter((_, k) => k !== i) }))}
        <ImageField label="Photo" url={x.url} onChange={v => set(i, { url: v })} />
      </div>))}
      {addBtn('Add photo', () => patch({ photos: [...ph, { url: '' }] }))}
    </>); }

  if (b.type === 'spacer') return (<Text label="Height (e.g. 40px)" value={b.size} onChange={v => patch({ size: v })} />);

  if (b.type === 'faq' || b.type === 'faq-v2') { const its = b.items || []; const set = (i, u) => { const n = its.slice(); n[i] = { ...n[i], ...u }; patch({ items: n }); };
    return (<>
      {b.type === 'faq' ? <Text label="Section heading" value={b.heading} onChange={v => patch({ heading: v })} /> : <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />}
      <label style={L}>Questions</label>
      {its.map((it, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Q', () => patch({ items: its.filter((_, k) => k !== i) }))}
        <Text label="Question" value={it.question} onChange={v => set(i, { question: v })} />
        <Area label="Answer (HTML allowed)" value={it.answer} rows={3} onChange={v => set(i, { answer: v })} />
      </div>))}
      {addBtn('Add question', () => patch({ items: [...its, { question: '', answer: '' }] }))}
    </>); }

  if (b.type === 'webpage-content') { const its = b.items || []; const set = (i, u) => { const n = its.slice(); n[i] = { ...n[i], ...u }; patch({ items: n }); };
    return (<>
      <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
      <Area label="Intro (HTML allowed)" value={b.intro} rows={3} onChange={v => patch({ intro: v })} />
      <label style={L}>Content sections</label>
      {its.map((it, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Section', () => patch({ items: its.filter((_, k) => k !== i) }))}
        <Text label="Heading" value={it.heading} onChange={v => set(i, { heading: v })} />
        <Area label="Body (HTML allowed)" value={it.content} rows={4} onChange={v => set(i, { content: v })} />
      </div>))}
      {addBtn('Add section', () => patch({ items: [...its, { heading: '', content: '' }] }))}
    </>); }

  if (b.type === 'og-banner') return (<>
    <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
    <Area label="Description (HTML allowed)" value={b.description} rows={4} onChange={v => patch({ description: v })} />
  </>);

  if (b.type === 'content-with-contact') return (<>
    <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
    <Area label="Description (HTML — include the button link)" value={b.description} rows={4} onChange={v => patch({ description: v })} />
  </>);

  if (b.type === 'image-with-legends') { const lg = b.legends || []; const set = (i, u) => { const n = lg.slice(); n[i] = { ...n[i], ...u }; patch({ legends: n }); };
    return (<>
      <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
      <label style={L}>Legend</label>
      {lg.map((l, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Item', () => patch({ legends: lg.filter((_, k) => k !== i) }))}
        <Text label="Color (e.g. rgba(50,141,236,1))" value={l.color} onChange={v => set(i, { color: v })} />
        <Text label="Label" value={l.label} onChange={v => set(i, { label: v })} />
      </div>))}
      {addBtn('Add legend item', () => patch({ legends: [...lg, { color: '', label: '' }] }))}
    </>); }

  if (b.type === 'cms-banner') return (<>
    <Text label="Title" value={b.title} onChange={v => patch({ title: v })} />
    <Area label="Description (HTML allowed)" value={b.description} rows={2} onChange={v => patch({ description: v })} />
    <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
    <ImageField label="Mobile image" url={b.mobileImage && b.mobileImage.url} onChange={v => patch({ mobileImage: { ...(b.mobileImage || {}), url: v } })} />
  </>);

  if (b.type === 'au-hero') { const ls = b.leads || []; const setL = (i, v) => { const n = ls.slice(); n[i] = v; patch({ leads: n }); };
    return (<>
      <Text label="Eyebrow" value={b.eyebrow} onChange={v => patch({ eyebrow: v })} />
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
      <Text label="Image alt text" value={b.image && b.image.alt} onChange={v => patch({ image: { ...(b.image || {}), alt: v } })} />
      <label style={L}>Lead paragraphs</label>
      {ls.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Para', () => patch({ leads: ls.filter((_, k) => k !== i) }))}
        <Area label="Text (HTML allowed)" value={t} rows={3} onChange={v => setL(i, v)} />
      </div>))}
      {addBtn('Add paragraph', () => patch({ leads: [...ls, ''] }))}
    </>); }

  if (b.type === 'au-proof') { const ts = b.tiles || []; const setT = (i, u) => { const n = ts.slice(); n[i] = { ...n[i], ...u }; patch({ tiles: n }); };
    return (<>
      <Text label="Badge (e.g. #1)" value={b.badge} onChange={v => patch({ badge: v })} />
      <Area label="Lead text (HTML allowed)" value={b.lead} rows={2} onChange={v => patch({ lead: v })} />
      <label style={L}>Stat tiles</label>
      {ts.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Tile', () => patch({ tiles: ts.filter((_, k) => k !== i) }))}
        <Text label="Number (HTML allowed)" value={t.num} onChange={v => setT(i, { num: v })} />
        <Text label="Label" value={t.label} onChange={v => setT(i, { label: v })} />
      </div>))}
      {addBtn('Add tile', () => patch({ tiles: [...ts, { num: '', label: '' }] }))}
      <Text label="CTA text" value={b.cta && b.cta.text} onChange={v => patch({ cta: { ...(b.cta || {}), text: v } })} />
      <Text label="CTA link" value={b.cta && b.cta.link} onChange={v => patch({ cta: { ...(b.cta || {}), link: v } })} />
    </>); }

  if (b.type === 'au-do') { const cs = b.columns || []; const setC = (i, u) => { const n = cs.slice(); n[i] = { ...n[i], ...u }; patch({ columns: n }); };
    return (<>
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <Text label="Subheading" value={b.sub} onChange={v => patch({ sub: v })} />
      <label style={L}>Columns</label>
      {cs.map((c, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Column', () => patch({ columns: cs.filter((_, k) => k !== i) }))}
        <Text label="Title" value={c.title} onChange={v => setC(i, { title: v })} />
        <Area label="Text (HTML allowed)" value={c.text} rows={3} onChange={v => setC(i, { text: v })} />
      </div>))}
      {addBtn('Add column', () => patch({ columns: [...cs, { title: '', text: '' }] }))}
    </>); }

  if (b.type === 'au-why') { const its = b.items || []; const setI = (i, v) => { const n = its.slice(); n[i] = v; patch({ items: n }); };
    return (<>
      <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
      <Text label="Image alt text" value={b.image && b.image.alt} onChange={v => patch({ image: { ...(b.image || {}), alt: v } })} />
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <label style={L}>List items</label>
      {its.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Item', () => patch({ items: its.filter((_, k) => k !== i) }))}
        <Area label="Item (HTML allowed; bold the lead-in)" value={t} rows={2} onChange={v => setI(i, v)} />
      </div>))}
      {addBtn('Add item', () => patch({ items: [...its, ''] }))}
    </>); }

  if (b.type === 'au-ship') { const ps = b.paragraphs || []; const bs = b.buttons || [];
    const setP = (i, v) => { const n = ps.slice(); n[i] = v; patch({ paragraphs: n }); };
    const setB = (i, u) => { const n = bs.slice(); n[i] = { ...n[i], ...u }; patch({ buttons: n }); };
    return (<>
      <Text label="Heading" value={b.heading} onChange={v => patch({ heading: v })} />
      <ImageField label="Image" url={b.image && b.image.url} onChange={v => patch({ image: { ...(b.image || {}), url: v } })} />
      <label style={L}>Paragraphs</label>
      {ps.map((t, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Para', () => patch({ paragraphs: ps.filter((_, k) => k !== i) }))}
        <Area label="Text (HTML allowed)" value={t} rows={3} onChange={v => setP(i, v)} />
      </div>))}
      {addBtn('Add paragraph', () => patch({ paragraphs: [...ps, ''] }))}
      <label style={L}>Buttons</label>
      {bs.map((bt, i) => (<div key={i} style={itemBox}>
        {itemHead(i, 'Button', () => patch({ buttons: bs.filter((_, k) => k !== i) }))}
        <Text label="Text" value={bt.text} onChange={v => setB(i, { text: v })} />
        <Text label="Link" value={bt.link} onChange={v => setB(i, { link: v })} />
      </div>))}
      {addBtn('Add button', () => patch({ buttons: [...bs, { text: '', link: '' }] }))}
    </>); }

  if (b.type === 'html') return (<Area label="Raw HTML" value={b.html} rows={6} onChange={v => patch({ html: v })} />);
  return <div style={{ fontSize: 12, color: '#b91c1c' }}>Unknown block type: {b.type}</div>;
}

// ── main editor ─────────────────────────────────────────────────────────────
export default function PageBlocksEditor({ pageType, pageKey, env, viewUrl, regions }) {
  const [blocks, setBlocks] = useState(null);
  const [orig, setOrig] = useState('[]');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [status, setStatus] = useState(null);
  const [addType, setAddType] = useState('content-block');

  const load = useCallback(async () => {
    if (!pageKey) return;
    setLoading(true); setStatus(null);
    try {
      const res = await neonQuery('page_content_get', { page_type: pageType, key: pageKey, env });
      const rows = Array.isArray(res) ? res : (res && res.rows) || [];
      const row = rows[0] || {};
      // Show the working draft if one exists, else the published content.
      const working = row.draft_content || row.content;
      const bl = working && Array.isArray(working.blocks) ? working.blocks : [];
      setBlocks(bl); setOrig(JSON.stringify(bl));
      // Unpublished changes exist if a draft is present and differs from published.
      setHasDraft(!!row.draft_content && JSON.stringify(row.draft_content) !== JSON.stringify(row.content));
    } catch (e) { setStatus({ type: 'error', msg: 'Load failed: ' + e.message }); setBlocks([]); }
    finally { setLoading(false); }
  }, [pageType, pageKey, env]);
  useEffect(() => { load(); }, [load]);

  const dirty = blocks && JSON.stringify(blocks) !== orig;
  const setBlock = (i, nb) => setBlocks(bs => bs.map((b, k) => (k === i ? nb : b)));
  const move = (i, d) => setBlocks(bs => { const n = bs.slice(); const j = i + d; if (j < 0 || j >= n.length) return n; const t = n[i]; n[i] = n[j]; n[j] = t; return n; });
  const remove = (i) => setBlocks(bs => bs.filter((_, k) => k !== i));
  const add = () => setBlocks(bs => [...(bs || []), (regions && regions.length ? { ...BLOCK_DEFAULTS[addType](), region: regions[0].value } : BLOCK_DEFAULTS[addType]())]);

  // Save = write the working DRAFT. Does not change the live storefront.
  const save = async () => {
    setSaving(true); setStatus(null);
    try {
      const res = await neonQuery('page_content_save_draft', { page_type: pageType, key: pageKey, env, content: { blocks }, updated_by: 'cpx-admin' });
      if (res && res.error) throw new Error(res.error);
      setOrig(JSON.stringify(blocks));
      setHasDraft(true);
      setStatus({ type: 'ok', msg: 'Draft saved to ' + env.toUpperCase() + '. Hit Publish to make it live.' });
    } catch (e) { setStatus({ type: 'error', msg: 'Save failed: ' + e.message }); }
    finally { setSaving(false); }
  };

  // Publish = make the draft live (render + push to the storefront). Saves first if dirty.
  const publish = async () => {
    setPublishing(true); setStatus(null);
    try {
      if (dirty) {
        const s = await neonQuery('page_content_save_draft', { page_type: pageType, key: pageKey, env, content: { blocks }, updated_by: 'cpx-admin' });
        if (s && s.error) throw new Error(s.error);
        setOrig(JSON.stringify(blocks));
      }
      const token = getAccessToken();
      const res = await fetch(BASE_URL + '/page-publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ page_type: pageType, key: pageKey, env }),
      });
      const j = await res.json();
      if (!res.ok || j.error) throw new Error(j.error || ('HTTP ' + res.status));
      setHasDraft(false);
      const bcMsg = j.bc && j.bc.track === 'A'
        ? (j.bc.published ? ' Published to the page body (server-rendered).' : ' (Page-body publish failed: ' + (j.bc.error || '?') + ')')
        : '';
      setStatus({ type: j.bc && j.bc.track === 'A' && !j.bc.published ? 'error' : 'ok', msg: 'Published to ' + env.toUpperCase() + '.' + bcMsg });
    } catch (e) { setStatus({ type: 'error', msg: 'Publish failed: ' + e.message }); }
    finally { setPublishing(false); }
  };

  if (!pageKey) return <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 0' }}>Select a {pageType === 'pdp' ? 'product' : 'category'} to edit its content blocks.</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: '#eef2ff', color: '#3730a3' }}>{env.toUpperCase()}</span>
        <span style={{ fontSize: 12, color: '#6b7280', flex: 1, minWidth: 120 }}>{pageKey} · {blocks ? blocks.length : 0} block{blocks && blocks.length === 1 ? '' : 's'}</span>
        {(hasDraft || dirty) && <span title="Unpublished changes" style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e' }}>Draft</span>}
        <button type="button" onClick={load} disabled={loading} title="Reload" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 9px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer' }}><RefreshCw size={13} className={loading ? 'spin' : ''} /> Reload</button>
        <button type="button" onClick={save} disabled={saving || !dirty} title="Save a draft (not live)" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: dirty ? '#2563eb' : '#9ca3af', padding: '6px 12px', border: '1px solid ' + (dirty ? '#2563eb' : '#d1d5db'), borderRadius: 5, background: '#fff', cursor: dirty && !saving ? 'pointer' : 'default' }}><Save size={14} /> {saving ? 'Saving…' : 'Save draft'}</button>
        <button type="button" onClick={publish} disabled={publishing || saving || (!dirty && !hasDraft)} title="Publish live" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: '#fff', padding: '6px 14px', border: 'none', borderRadius: 5, background: (dirty || hasDraft) ? '#059669' : '#9ca3af', cursor: (dirty || hasDraft) && !publishing ? 'pointer' : 'default' }}><Upload size={14} /> {publishing ? 'Publishing…' : 'Publish'}</button>
        {viewUrl && <a href={viewUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#2563eb', textDecoration: 'none' }}><ExternalLink size={13} /> View</a>}
      </div>

      {loading && blocks === null ? <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div> : (blocks || []).map((b, i) => (
        <div key={b.id || i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 10, background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', flex: 1 }}>{i + 1}. {BLOCK_LABELS[b.type] || b.type}</span>
            {regions && regions.length > 0 && (
              <select value={b.region || regions[0].value} onChange={e => setBlock(i, { ...b, region: e.target.value })} title="Region" style={{ fontSize: 11.5, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 4 }}>
                {regions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}
            {pageType === 'pdp' && (
              <select value={b.region || 'main'} onChange={e => setBlock(i, { ...b, region: e.target.value === 'main' ? undefined : e.target.value })} title="Position relative to reviews" style={{ fontSize: 11.5, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 4 }}>
                <option value="main">Above reviews</option>
                <option value="below">Below reviews</option>
              </select>
            )}
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up" style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? '#d1d5db' : '#6b7280' }}><ArrowUp size={15} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === (blocks.length - 1)} title="Move down" style={{ border: 'none', background: 'none', cursor: i === (blocks.length - 1) ? 'default' : 'pointer', color: i === (blocks.length - 1) ? '#d1d5db' : '#6b7280' }}><ArrowDown size={15} /></button>
            <button type="button" onClick={() => remove(i)} title="Delete block" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b91c1c' }}><Trash2 size={15} /></button>
          </div>
          <BlockForm block={b} onChange={nb => setBlock(i, nb)} />
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <select value={addType} onChange={e => setAddType(e.target.value)} style={{ fontSize: 13, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 5 }}>
          {Object.keys(BLOCK_LABELS).map(t => <option key={t} value={t}>{BLOCK_LABELS[t]}</option>)}
        </select>
        <button type="button" onClick={add} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, padding: '6px 12px', border: '1px solid #2563eb', color: '#2563eb', borderRadius: 5, background: '#fff', cursor: 'pointer' }}><Plus size={14} /> Add block</button>
      </div>

      {status && <div style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: status.type === 'ok' ? '#047857' : '#b91c1c' }}>{status.type === 'ok' ? <Check size={15} /> : <AlertTriangle size={15} />} {status.msg}</div>}
    </div>
  );
}
